// ─── Gramática del diccionario de TurnosIA — FUENTE ÚNICA del artículo ───────
//
// El diccionario guarda la PALABRA ("Sucursal") y UNA LETRA de género
// ("f"). De esas dos cosas se derivan acá los artículos: definido (el/la),
// indefinido (un/una) y las contracciones (del/de la, al/a la), en singular y
// en plural, para los cuatro idiomas.
//
// ⚠ POR QUÉ NO SE GUARDA EL ARTÍCULO DENTRO DEL VALOR:
// si el admin escribiera "la Sucursal", después harían falta "una Sucursal",
// "las Sucursales" y "La Sucursal" en mayúscula → cuatro campos por palabra,
// cargados a mano, inconsistentes desde el primer día. Con la letra salen los
// cuatro solos y no se pueden desincronizar.
//
// ⚠ NUNCA concatenar `"el " + tt('professional')` en un componente. Todo
// artículo sale de acá. Si aparece un caso nuevo (otra preposición, otro
// idioma), se agrega en este archivo, no en la pantalla.
//
// ⚠ ALCANCE: esto resuelve el ARTÍCULO, no la concordancia de adjetivos
// ("asignado/asignada", "listo/lista", "mismo/misma"). Para eso la regla del
// proyecto es otra: FRASES NEUTRAS que funcionen en cualquier rubro sin
// sustituir nada. Las dos técnicas se complementan — el género para etiquetas
// cortas donde el artículo es inevitable, la neutralidad para la prosa.

// Idiomas soportados. Cualquier otro cae a "es".
const LANGS = ["es", "it", "en", "pt"];

export const normalizeLang = (locale) => {
  const base = String(locale || "").toLowerCase().split(/[-_]/)[0];
  return LANGS.includes(base) ? base : "es";
};

// Género efectivo de un sustantivo. Default "m" (comportamiento previo: es el
// género de la mayoría de los defaults y el que no cambia ningún texto).
export const normalizeGender = (g) => (String(g || "").toLowerCase() === "f" ? "f" : "m");

const firstLetter = (w) => String(w || "").trim().charAt(0);
const lower = (w) => String(w || "").trim().toLowerCase();

// ─── Español ────────────────────────────────────────────────────────────────
// Regla del "á tónica": un sustantivo FEMENINO que empieza con /a/ tónica lleva
// el artículo masculino en SINGULAR ("el área", "un aula") pero femenino en
// plural ("las áreas") y en las contracciones se comporta como masculino
// ("del área", "al área"). Detectamos el caso con acento escrito ("Área",
// "Águila"), que es el que aparece en el diccionario (rubro taller usa "Área").
// ⚠ Aproximación conocida: "agua"/"aula"/"hacha" (a tónica SIN tilde) no se
// detectan — no hay forma de saber la sílaba tónica desde el string.
const esNeedsMasculineArticle = (word, gender) =>
  gender === "f" && /^[áÁ]|^h[áÁ]/.test(String(word || "").trim());

const es = (word, gender, plural) => {
  const aTonica = !plural && esNeedsMasculineArticle(word, gender);
  const f = gender === "f" && !aTonica;
  if (plural) {
    return gender === "f"
      ? { def: "las", indef: "unas", de: "de las", a: "a las" }
      : { def: "los", indef: "unos", de: "de los", a: "a los" };
  }
  return f
    ? { def: "la", indef: "una", de: "de la", a: "a la" }
    : { def: "el", indef: "un", de: "del", a: "al" };
};

// ─── Portugués ──────────────────────────────────────────────────────────────
const pt = (word, gender, plural) => {
  if (plural) {
    return gender === "f"
      ? { def: "as", indef: "umas", de: "das", a: "às" }
      : { def: "os", indef: "uns", de: "dos", a: "aos" };
  }
  return gender === "f"
    ? { def: "a", indef: "uma", de: "da", a: "à" }
    : { def: "o", indef: "um", de: "do", a: "ao" };
};

// ─── Inglés ─────────────────────────────────────────────────────────────────
// Sin género. Lo único que varía es a/an, por SONIDO inicial; se aproxima por
// letra vocal, que acierta salvo excepciones fonéticas ("an hour", "a user").
const enIndef = (word) => (/^[aeiou]/i.test(lower(word)) ? "an" : "a");
const en = (word, _gender, plural) => ({
  def: "the",
  indef: plural ? "some" : enIndef(word),
  de: "of the",
  a: "to the"
});

// ─── Italiano ───────────────────────────────────────────────────────────────
// El caso más rico: el artículo depende del género Y del arranque fonético.
//   masculino: lo/gli antes de s+consonante, z, gn, ps, pn, x, y, i+vocal;
//              l'/gli antes de vocal; il/i en el resto.
//   femenino:  l'/le antes de vocal; la/le en el resto.
// ⚠ APROXIMACIONES DOCUMENTADAS (asumidas, no ocultas):
//   · "h" inicial: en italiano casi todas son extranjerismos ("l'hotel").
//     Tratamos h- como vocal → "l'hotel"/"gli hotel". Correcto para el uso real.
//   · "i + vocal" ("lo iodio") se detecta por regex /^i[aeou]/, que también
//     agarraría alguna palabra rara; es el criterio estándar.
//   · Semiconsonante "u" ("un uomo" lleva l'uomo) queda como vocal → correcto.
const itLoCase = (w) => /^(s[^aeiouàèéìòù]|z|gn|ps|pn|x|y|i[aeou])/i.test(w);
const itVowel = (w) => /^[aeiouàèéìòùh]/i.test(w);

const it = (word, gender, plural) => {
  const w = lower(word);
  if (gender === "f") {
    if (plural) return { def: "le", indef: "delle", de: "delle", a: "alle" };
    return itVowel(w)
      ? { def: "l'", indef: "un'", de: "dell'", a: "all'", elided: true }
      : { def: "la", indef: "una", de: "della", a: "alla" };
  }
  if (plural) {
    return itLoCase(w) || itVowel(w)
      ? { def: "gli", indef: "degli", de: "degli", a: "agli" }
      : { def: "i", indef: "dei", de: "dei", a: "ai" };
  }
  if (itVowel(w)) return { def: "l'", indef: "un", de: "dell'", a: "all'", elided: true };
  if (itLoCase(w)) return { def: "lo", indef: "uno", de: "dello", a: "allo" };
  return { def: "il", indef: "un", de: "del", a: "al" };
};

const ENGINES = { es, it, en, pt };

/**
 * Artículo suelto para una palabra.
 * @param {string} word    la palabra del diccionario ("Sucursal")
 * @param {string} gender  "m" | "f" (default "m")
 * @param {object} opts    { lang, form: "def"|"indef"|"de"|"a", plural, caps }
 * @returns {string} el artículo ya listo ("la", "las", "de la", "l'"…)
 */
export const article = (word, gender, opts = {}) => {
  const lang = normalizeLang(opts.lang);
  const g = normalizeGender(gender);
  const set = ENGINES[lang](word, g, !!opts.plural);
  const raw = set[opts.form || "def"] || set.def;
  return opts.caps ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw;
};

/**
 * Artículo + palabra, con el espacio correcto. En italiano el artículo elidido
 * (l', un', dell') se pega SIN espacio — por eso esto vive acá y no en cada
 * componente.
 * @returns {string} "la Sucursal" · "l'appuntamento" · "the room"
 */
export const withArticle = (word, gender, opts = {}) => {
  const lang = normalizeLang(opts.lang);
  const g = normalizeGender(gender);
  const set = ENGINES[lang](word, g, !!opts.plural);
  let art = set[opts.form || "def"] || set.def;
  if (opts.caps) art = art.charAt(0).toUpperCase() + art.slice(1);
  const glue = art.endsWith("'") ? "" : " ";
  return `${art}${glue}${word}`;
};

/** Primera letra en mayúscula, respetando el resto (para títulos y etiquetas). */
export const capitalize = (s) => {
  const v = String(s == null ? "" : s);
  return v ? v.charAt(0).toUpperCase() + v.slice(1) : v;
};

export default { article, withArticle, capitalize, normalizeLang, normalizeGender };
